import { TTableColumnsFilter } from '@shared/components/Table';
import { IBaseResponse, IDataResponse } from '@shared/types/fetch-data';

export type TRoutingConfigVersionStatus = 'draft' | 'published' | 'archived';

// Who performed the action: an admin ({type: "App\Models\Admin", reference: <uuid>}),
// an integration client ({type: "QA", reference: <email>}), or nobody yet (all null).
// value carries the resolved display identity (admin email) since CSP-648.
export interface IRoutingConfigActor {
  type: string | null;
  reference: string | null;
  value: string | null;
}

export interface IRoutingConfigRuleComparator {
  eq?: number;
  gt?: number;
  gte?: number;
  lt?: number;
  lte?: number;
  tags?: string[];
}

export type TRoutingConfigRuleParamValue = string[] | IRoutingConfigRuleComparator;

export interface IRoutingConfigRuleParam {
  name: string;
  type: 'multi-select' | 'digit-compare' | 'tag-digit-compare' | string;
  rawValue: TRoutingConfigRuleParamValue;
  parsedValue: TRoutingConfigRuleParamValue | Record<string, unknown>;
}

export interface IRoutingConfigRule {
  id: number;
  name: string;
  description: string | null;
  active: boolean;
  min: number;
  max: number;
  conditions: IRoutingConfigRuleParam[];
  selections: IRoutingConfigRuleParam[];
  sort_strategy: string;
  priority: number;
  priority_auto: number | null;
}

// currency/method/traffic arrive as an array OR a bare string (same duality as
// the legacy IPolicyConditionPayload); merchants is array-only.
export interface IRoutingConfigPolicyCondition {
  currency?: string[] | string;
  method?: string[] | string;
  traffic?: string[] | string;
  merchants?: string[];
}

export interface IRoutingConfigPolicy {
  id: number;
  name: string;
  description: string | null;
  active: boolean;
  conditions: IRoutingConfigPolicyCondition[];
  priority: number;
  priority_auto: number | null;
  rules: IRoutingConfigRule[];
}

export interface IRoutingConfigVersion {
  id: number;
  name: string;
  status: TRoutingConfigVersionStatus;
  comment: string | null;
  published_at: string | null;
  // Serialized as "YYYY-MM-DD HH:mm:ss" in UTC without a timezone marker
  // (unlike the ISO published_at) — parse via parseUtcDateTime (CSP-645).
  created_at: string;
  updated_at: string;
  parent_version_id: number | null;
  created_by: IRoutingConfigActor;
  updated_by: IRoutingConfigActor;
  policies: IRoutingConfigPolicy[];
}

export type IRoutingConfigVersionsResponse = IDataResponse<IRoutingConfigVersion>;
export type IRoutingConfigVersionResponse = IBaseResponse<IRoutingConfigVersion>;

export type IRoutingVersionsTableColumns = {
  version: string;
  name: string;
  status: string;
  author: string;
  created_at: string;
  published_at: string;
  comment: string;
};

export type IRoutingConfigPoliciesTableColumns = {
  expand: string;
  priority: string;
  name: string;
  status: string;
  traffic: string;
  rules: string;
  // Edit mode only.
  actions?: string;
};

export type IRoutingVersionsFilter = TTableColumnsFilter<IRoutingVersionsTableColumns>;

// ── Write side ─────────────────────────────────────────────────────────────

// POST /routing/config/versions/draft — clones the source (default: the active
// version) into a new draft. `comment` is ignored by the backend.
export interface IRoutingConfigDraftCreateBody {
  name: string;
  source_version_id?: number;
}

// PUT /routing/config/versions/{id} — full config replace on a draft: no ids,
// positional priorities (index + 1), rule params as {techname: rawValue} objects.
export interface IRoutingConfigRulePayload {
  name: string;
  description: string | null;
  active: boolean;
  min: number;
  max: number;
  conditions: Record<string, TRoutingConfigRuleParamValue>;
  selections: Record<string, TRoutingConfigRuleParamValue>;
  sort_strategy: string;
  priority: number;
}

export interface IRoutingConfigPolicyPayload {
  name: string;
  description: string | null;
  active: boolean;
  conditions: IRoutingConfigPolicyCondition[];
  priority: number;
  rules: IRoutingConfigRulePayload[];
}

export interface IRoutingConfigPutBody {
  policies: IRoutingConfigPolicyPayload[];
}

export interface IRoutingConfigUpdateQuery {
  id: number;
  body: IRoutingConfigPutBody;
}

// PATCH /routing/config/versions/{id} — metadata only, any status, partial since
// CSP-647: both fields optional, `name` <= 150, `comment` <= 512. Clearing the
// comment needs an explicit null — an empty string leaves the old value in place.
export interface IRoutingConfigMetadataBody {
  name?: string;
  comment?: string | null;
}

export interface IRoutingConfigMetadataQuery {
  id: number;
  body: IRoutingConfigMetadataBody;
}

// POST /routing/config/versions/{id}/publish — the comment is saved in the same
// transaction as the publish. Optional for the backend, required by the
// FE: it is the audit trail entry.
export const PUBLISH_COMMENT_MIN_LENGTH = 3;
export const PUBLISH_COMMENT_MAX_LENGTH = 255;

export interface IRoutingConfigPublishBody {
  comment: string;
}

export interface IRoutingConfigPublishQuery {
  id: number;
  body: IRoutingConfigPublishBody;
}

// ── Editor (client-held edit session) ──────────────────────────────────────

// Array-only dimensions (the API also emits bare strings), so untouched policies never diff.
export interface IEditorPolicyCondition {
  currency: string[];
  method: string[];
  traffic: string[];
  merchants: string[];
}

// Client id the diff matches on: String(id) for server nodes, generated for new ones (no id).
export interface IEditorRule extends Omit<IRoutingConfigRule, 'id'> {
  uid: string;
  id?: number;
}

export interface IEditorPolicy extends Omit<IRoutingConfigPolicy, 'id' | 'conditions' | 'rules'> {
  uid: string;
  id?: number;
  conditions: IEditorPolicyCondition[];
  rules: IEditorRule[];
}

// What the policy form edits.
export interface IEditorPolicyInput {
  name: string;
  description: string | null;
  conditions: IEditorPolicyCondition[];
}

// What the rule form edits; `active` stays a row-level toggle, like the policy's.
// Params keep the GET array shape so toPutPayload serialises new and server rules alike.
export interface IEditorRuleInput {
  name: string;
  description: string | null;
  min: number;
  max: number;
  conditions: IRoutingConfigRuleParam[];
  selections: IRoutingConfigRuleParam[];
  sort_strategy: string;
}

// Bumped when the persisted shape changes; older sessions are dropped on load.
// 2: conditions are array-only.
export const EDITOR_SESSION_SCHEMA_VERSION = 2;

// One edit session, persisted per version id. `savedDraftId` is the PUT target:
// the base when editing a draft, else the draft Save Draft creates.
export interface IEditorSession {
  schemaVersion: typeof EDITOR_SESSION_SCHEMA_VERSION;
  versionId: number;
  baseStatus: TRoutingConfigVersionStatus;
  // Guards against edits made elsewhere: a mismatch on hydrate drops the session.
  baseUpdatedAt: string;
  original: IEditorPolicy[];
  draft: IEditorPolicy[];
  savedDraftId: number | null;
}

export interface IEditorDiff {
  // One change per policy changed, moved, added or removed.
  count: number;
  // Uids of draft policies that differ from the original (changed / moved / added).
  changedUids: string[];
  // Subset of changedUids: added policies.
  addedUids: string[];
  // Rule-level marks, for rules of policies present on both sides: changed / moved / added.
  // Rules of an added policy carry none — the policy's own mark covers them.
  changedRuleUids: string[];
  // Subset of changedRuleUids: added rules.
  addedRuleUids: string[];
  isDirty: boolean;
  // One line per change, draft order, removed last: "XMB_Off → on", "PD_KNX → edited".
  summary: string[];
}

// A validation problem keyed by config path (`policies.0.rules.1.max`), so client
// and server issues share one renderer.
export interface IConfigIssue {
  path: string;
  message: string;
}

// Derived per render from the session: the diff plus the local validation result.
export interface IEditorChanges extends IEditorDiff {
  issues: IConfigIssue[];
}
